import { getNodeTypeConfig, getNodeTypeConfigByPaletteKey } from '../core/config/node-types';
import type { NodeData } from '../types/flow-types';

export function formatFlowCategoryTitle(raw: string): string {
	return raw.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function resolveFlowNodeTypeLabel(nodeType: string, data: Pick<NodeData, 'paletteKey'>): string {
	const paletteKey = String(data.paletteKey || nodeType);
	return (
		getNodeTypeConfigByPaletteKey(paletteKey)?.label ??
		getNodeTypeConfig(nodeType)?.label ??
		formatFlowCategoryTitle(nodeType)
	);
}

export function resolveFlowConfiguredSummary(
	data: Pick<NodeData, 'displayLabel' | 'label'>,
	fallback: string,
): string {
	return String(data.displayLabel || data.label || fallback).trim() || fallback;
}

export function resolveTimeConditionSummary(data: NodeData): string {
	const subType = String(data.timeSubType || data.subType || 'daily');
	if (subType === 'timer') {
		const interval = Math.max(1, Number(data.interval) || 1);
		return interval === 1 ? 'Every minute' : `Every ${interval} minutes`;
	}
	if (subType === 'ot_cond' || subType === 'execute_once') {
		const date = String(data.date || '').trim();
		const time = String(data.executeTime || data.time || '').trim();
		return [date ? `Once on ${date}` : 'Once', time ? `at ${time}` : ''].filter(Boolean).join(' ');
	}

	const time = String(data.time || '08:00');
	const weekdays = Array.isArray(data.weekdays) ? (data.weekdays as number[]) : [];
	const weekdayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	const days = weekdays.map((day) => weekdayNames[Number(day)]).filter(Boolean);
	const daySummary =
		days.length === 7 ? 'daily' : days.length > 0 ? days.join(', ') : 'on configured days';
	return `At ${time} · ${daySummary}`;
}

export function resolveManualConditionSummary(data: NodeData): string {
	const paletteKey = String(data.paletteKey || 'manualCondition');
	const customLabel = String(data.displayLabel || '').trim();
	const defaultLabels = new Set([
		'Manual Trigger',
		'Webhook Trigger',
		'Manual Execution',
		'Manual execution only',
	]);
	if (customLabel && !defaultLabels.has(customLabel)) return customLabel;
	return paletteKey === 'webhookTrigger'
		? 'Run when the webhook is called'
		: 'Run when started manually';
}

const FLOW_OPERATOR_LABELS: Record<string, string> = {
	eq: '=',
	equal: '=',
	ne: '≠',
	neq: '≠',
	lt: '<',
	less_than: '<',
	'less than': '<',
	gt: '>',
	greater_than: '>',
	'greater than': '>',
	le: '≤',
	ge: '≥',
};

function humanizeFlowValue(value: unknown): string {
	return String(value ?? '')
		.replace(/[_|]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function compactDaySummary(days: number[]): string {
	const normalized = [...new Set(days.map(Number).filter((day) => day >= 0 && day <= 6))].sort();
	if (normalized.length === 7) return 'Daily';
	if (normalized.join(',') === '1,2,3,4,5') return 'Mon–Fri';
	if (normalized.join(',') === '0,6') return 'Weekends';
	const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	return normalized.map((day) => names[day]).join(', ') || 'Selected days';
}

export function resolveScheduleConditionSummary(data: NodeData): string {
	const intervals = Array.isArray(data.activeTimeIntervals)
		? (data.activeTimeIntervals as Array<Record<string, unknown>>)
		: [];
	if (!intervals.length) return 'Add a time window';
	const first = intervals[0];
	const from = String(first.from ?? first.start ?? '').trim();
	const to = String(first.to ?? first.end ?? '').trim();
	const days = Array.isArray(first.days) ? (first.days as number[]) : [];
	const window = from && to ? `${from}–${to}` : 'Configured time';
	const firstSummary = `${compactDaySummary(days)} · ${window}`;
	return intervals.length === 1 ? firstSummary : `${firstSummary} · +${intervals.length - 1} more`;
}

export function resolveSunConditionSummary(data: NodeData): string {
	const sunType = String(data.sunType || data.type || 'sunrise').toLowerCase();
	const event = sunType === 'sunset' ? 'sunset' : 'sunrise';
	const direction =
		String(data.beforeAfter || data.before_after || 'after') === 'before' ? 'before' : 'after';
	const minutes = Math.max(
		0,
		Number(data.offsetMinutes ?? data.offset_minutes) ||
			Number(data.hours || 0) * 60 + Number(data.minutes ?? data.mins ?? 0),
	);
	if (!minutes) return `At ${event}`;
	const duration =
		minutes % 60 === 0
			? `${minutes / 60} hr`
			: minutes > 60
				? `${Math.floor(minutes / 60)} hr ${minutes % 60} min`
				: `${minutes} min`;
	return `${duration} ${direction} ${event}`;
}

export function resolveEnergyConditionSummary(data: NodeData): string {
	const market = humanizeFlowValue(data.exchangeMarket ?? data.exchange_market ?? 'Energy');
	const operator =
		FLOW_OPERATOR_LABELS[String(data.operator || 'less than')] || String(data.operator || '<');
	const value = data.value === '' || data.value == null ? '?' : String(data.value);
	const offset = Number(data.minutes || 0);
	return `${market} · ${operator} ${value} EUR/MWh${offset ? ` · +${offset} min` : ''}`;
}

export function resolveWeatherConditionSummary(data: NodeData): string {
	const condition = humanizeFlowValue(data.weatherCond ?? data.cond ?? 'weather');
	const rawValue = data.value;
	if (rawValue === '' || rawValue == null) return `Choose ${condition.toLowerCase()} value`;
	const operator = FLOW_OPERATOR_LABELS[String(data.operator ?? data.op ?? 'equal')] || '=';
	const offset = Number(data.hourOffset ?? data.hours ?? 0);
	return `${condition} ${operator} ${humanizeFlowValue(rawValue)}${offset ? ` · in ${offset} hr` : ' · now'}`;
}

export function resolveVariableConditionSummary(data: NodeData): string {
	const name = String(data.variableName || '').trim();
	if (!name) return 'Choose a variable';
	const operator = FLOW_OPERATOR_LABELS[String(data.operator || 'eq')] || String(data.operator || '=');
	const value =
		data.variableValue === '' || data.variableValue == null ? '?' : String(data.variableValue);
	return `${name} ${operator} ${value}`;
}

export function resolveSetVariableSummary(data: NodeData): string {
	const name = String(data.variable?.name || '').trim();
	if (!name) return 'Choose a variable';
	const value =
		data.variable?.value === '' || data.variable?.value == null
			? 'empty'
			: String(data.variable.value);
	return `Set ${name} to ${value}`;
}

export function resolveMathConditionSummary(data: NodeData): string {
	const expression = String(data.expression || data.sceneData?._gui_math_expression || '').trim();
	return expression || 'Build an expression';
}
